import type { Game } from '../core/game';
import type { Affix, AffixId, MasterDie } from '../core/types';
import { AFFIX_BY_ID } from '../data/affixes';
import { BALANCE } from '../data/balance';
import { affixValueOf, enrichedFactor, isEnriched } from './abyssApi';

/**
 * Explications d'affixes destinées au joueur (menu Dés — COMMENTS §6.1).
 *
 * `src/data/affixes.ts` porte déjà une `description`, mais générique (« X %
 * de… ») : elle sert de référence de règle, pas d'affichage. Cette table-ci
 * produit une phrase française complète **avec la valeur du dé injectée**,
 * alignée sur les règles de COMMENTS §4 (Temporel = chance de gagner des secondes — `BALANCE.temporalSeconds`,
 * Empoisonné = DoT d'un pourcentage des dégâts du lancer).
 */

/**
 * Constantes de règles lues de façon tolérante : `src/data/balance.ts` évolue
 * (COMMENTS v0.2.2) et l'affichage ne doit jamais dépendre d'une clé précise.
 */
const B = BALANCE as unknown as Record<string, number | undefined>;
/** Durée du poison, en secondes (COMMENTS §4). */
const POISON_SECONDS = B.poisonDuration ?? 4;
/** Secondes gagnées par un déclenchement de *Temporel* (COMMENTS §4). */
const TEMPORAL_SECONDS = B.temporalSeconds ?? 1;
/** Seuil de l'affixe *Exécutant* : PV de la cible au-dessus de cette part (A122-1). */
const EXECUTIONER_PERCENT = Math.round((B.executionerHpAbove ?? 0.8) * 100);
/** *ArchiDicer* (A122-7) : bonus de dégâts par sort et plafond, en pourcentage. */
const ARCHIDICER_PER_SPELL = Math.round((B.archidicerPerSpell ?? 0.33) * 100);
const ARCHIDICER_CAP = Math.round((B.archidicerCap ?? 1) * 100);
/** *Rat-Ailé* (A123-4) : amélioration des affixes, en pourcentage (20). */
const WINGED_RAT_PERCENT = Math.round(((B.wingedRatAffixFactor ?? 1.2) - 1) * 100);
/** *Croupier* (A123-3) : les deux multiplicateurs, en pourcentage (1 et 400). */
const CROUPIER_LOW_PERCENT = Math.round((B.croupierLow ?? 0.01) * 100);
const CROUPIER_HIGH_PERCENT = Math.round((B.croupierHigh ?? 4) * 100);
/** Durée de la mutilation, en secondes (COMMENTS v0.6.0 §1.3). */
const MUTILATE_SECONDS = B.affixMutilatedDuration ?? B.mutilateDuration ?? 3;
/** Dégâts supplémentaires subis par un ennemi mutilé, en pourcentage. */
const MUTILATE_BONUS = Math.round((((B.affixMutilatedFactor ?? 1.3) as number) - 1) * 100);

/** Nombre en français : virgule décimale, pas de zéro inutile. */
function fr(n: number, decimals = 1): string {
  return n.toFixed(decimals).replace(/\.0$/, '').replace('.', ',');
}

/** Valeur d'un affixe formatée selon l'`unit` de sa définition. */
export function affixValueText(affix: Affix): string {
  const def = AFFIX_BY_ID[affix.id];
  if (!def) return String(affix.value);
  if (def.unit === 'percent') return `${Math.round(affix.value * 100)} %`;
  if (def.unit === 'seconds') return `${fr(affix.value)} s`;
  // v0.26.0 (A72-14) : l'affixe nommé d'un Prismatique est **unique**.
  if (def.named) return 'unique';
  return 'actif';
}

/** Nom français d'un affixe. */
export function affixName(id: AffixId): string {
  return AFFIX_BY_ID[id]?.name ?? id;
}

type Explain = (value: number) => string;

/**
 * Table d'explications. **Partielle par construction** : l'agent `src/core`
 * ajoute des affixes (v0.7.0 : *Mutilant*, *Pipé*) et une table exhaustive
 * ferait échouer la compilation de l'interface à chaque ajout, alors qu'un
 * affixe inconnu retombe très bien sur la `description` de `src/data/affixes`.
 */
const EXPLAIN: Partial<Record<AffixId, Explain>> & Record<string, Explain | undefined> = {
  weighted: (v) =>
    `${Math.round(v * 100)} % de chance que ce dé tombe directement sur sa face maximale.`,
  incisive: (v) =>
    `Ajoute +${fr(v, 2)} au multiplicateur de dégâts critiques du lancer.`,
  precise: (v) =>
    `Ajoute +${Math.round(v * 100)} % de chance de critique au lancer.`,
  executioner: (v) =>
    `+${Math.round(v * 100)} % de dégâts quand la cible a plus de ${EXECUTIONER_PERCENT} % de ses points de vie.`,
  temporal: (v) =>
    `${Math.round(v * 100)} % de chance de gagner ${fr(TEMPORAL_SECONDS)} s de chrono à la mort d'un ennemi.`,
  golden: (v) => `+${Math.round(v * 100)} % d'or sur l'ennemi tué par ce lancer.`,
  poisoned: (v) =>
    `Empoisonne la cible : ${Math.round(v * 100)} % des dégâts du lancer par seconde pendant ${POISON_SECONDS} s.`,
  advantaged: () => 'Ce dé est lancé deux fois : seule la meilleure face est conservée.',
  // v0.7.0 (COMMENTS v0.6.0 §1.3) — réservés aux d12.
  mutilating: (v) =>
    `${Math.round(v * 100)} % de chance de mutiler l'ennemi pendant ${MUTILATE_SECONDS} s : il subit alors +${MUTILATE_BONUS} % de dégâts.`,
  loaded: () =>
    'La moitié des faces du dé est remplacée par une même face : le dé se comporte comme une pièce.',
  // v0.26.0 (A72-14 à 17) — affixes uniques des dés Prismatiques nommés.
  guinsoo: () =>
    'Tous les 3 lancers où ce dé est lancé, les dégâts du lancer sont appliqués deux fois (deux coups).',
  foreman: () =>
    'Un tractopelle se charge à chaque lancer d’autant de dés Basiques que votre contenant ; le bouton « Lancer de tractopelle » les ajoute au prochain lancer, puis le vide.',
  // v0.49.0 (A98-7) — Réplicant : une copie du dé à chaque lancer, jamais récursive.
  replicant: () =>
    'Une copie de ce dé est lancée en plus à chaque lancer ; la copie n’est pas répliquée à son tour.',
  // v0.57.0 (A122-7) : +33 % de dégâts par sort du lancer, +100 % au plus.
  archidicer: () =>
    `Chaque sort déclenché par le lancer (sorts d’emplacement et de dé, copies comprises) augmente ses dégâts de ${ARCHIDICER_PER_SPELL} % ; le bonus ne peut pas dépasser +${ARCHIDICER_CAP} %.`,
  // v0.58.0 (A123) — trois nouveaux dés Prismatiques nommés (textes du GD).
  andariel: () =>
    'Apparaît toujours avec l’affixe Empoisonné. Si le poison appliqué à l’ennemi excède ses points de vie restants, le poison sur la cible est doublé (une fois par monstre). Un seul Dé d’Andarielle équipé à la fois.',
  wingedRat: () =>
    `Ne sort qu’aux Corrupted GachaDice. Ses affixes à plage sortent à leur valeur maximale. Si vous n’avez aucun bonus de Matériau actif, les affixes de vos dés équipés sont améliorés de ${WINGED_RAT_PERCENT} % (multiplicatif ; plafond du poison compris). Un seul Dé du Rat-Ailé équipé à la fois.`,
  croupier: () =>
    `À chaque lancer, vos dégâts sont multipliés par ${CROUPIER_LOW_PERCENT} % ou par ${CROUPIER_HIGH_PERCENT} % (une chance sur deux). Un seul Dé du Croupier équipé à la fois.`,
};

/** Phrase d'explication d'un affixe, valeur du dé comprise. */
export function affixExplanation(affix: Affix): string {
  const explain = EXPLAIN[affix.id];
  return explain ? explain(affix.value) : (AFFIX_BY_ID[affix.id]?.description ?? affix.id);
}

/** Ligne prête à afficher : nom, valeur formatée, explication. */
export interface AffixLine {
  id: AffixId;
  name: string;
  value: string;
  explanation: string;
}

/**
 * Lignes d'un dé. Avec le dé (A94), la valeur est **celle que le dé porte**
 * (`affixValueOf`, Enrichi compris) : « 33 % (×1,5) » et une explication écrite
 * avec cette valeur. Sans lui, la valeur brute, comme avant. v0.58.0 (A123-4) :
 * le *Rat-Ailé* actif relève aussi les dés équipés — « 24 % (×1,2) », et
 * « 36 % (×1,8) » sur un dé Enrichi.
 */
export function affixLines(affixes: readonly Affix[], on?: { die: MasterDie; game?: Game | null }): AffixLine[] {
  return affixes.map((a) => {
    const boosted = on ? affixValueOf(on.game, on.die, a) : a.value;
    if (!on || (!isEnriched(on.die) && boosted === a.value)) {
      return { id: a.id, name: affixName(a.id), value: affixValueText(a), explanation: affixExplanation(a) };
    }
    const effective: Affix = { ...a, value: boosted };
    const unit = AFFIX_BY_ID[a.id]?.unit;
    const ratio = a.value > 0 && boosted !== a.value ? boosted / a.value : enrichedFactor();
    const factor = unit === 'percent' || unit === 'seconds' ? ` (×${fr(ratio, 2).replace(/,?0+$/, '')})` : '';
    return {
      id: a.id,
      name: affixName(a.id),
      value: `${affixValueText(effective)}${factor}`,
      explanation: affixExplanation(effective),
    };
  });
}
